import { observer } from 'mobx-react';
import { FC } from 'react';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { Page, Pagebar, PagebarTitle, PagebarActions } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useCampaignGroupActions } from '../../../modules/hooks/actions/campaign-group.actions';
import {
	IPerformanceOptionsDateSegments,
	IPerformanceOptionsSortBy,
	IPerformanceOptionsSortOrder,
	PerformancesColumnsSnakeCase,
	groupByMapper,
	useReportsList,
} from '../../../modules/hooks/apis/use-reports-list';
import { Outlet } from 'react-router-dom';
import { useMetrics } from '../../../modules/hooks/tools/use-metrics';
import { Chart } from '@monorepo/base/src/components/chart/chart';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { useCampaignGroupHeaders } from '../../../modules/hooks/theaders/campaign-groups.headers';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { IAdminxStore } from '../../../modules/stores';
import { availablePerformanceToMetrics } from '../../../modules/models/performance.model';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Card } from '@monorepo/base/src/components/card/card';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';

const actionsDebugProps = { dataAttrs: [new DataAttribute('id', 'campaign_groups_actions')] };
const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'pagebar')] };
const tableDebugProps = { dataAttrs: [new DataAttribute('id', 'campaign_groups_table')] };

export const CampaignGroups: FC = observer(() => {
	const { campaignGroupsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const metricsStore = campaignGroupsStore.getMetrics();
	const reportList = campaignGroupsStore.getListStore();
	const columnVisibility = usePageSettings().tableStore?.getColumnVisibility();
	const defaultSortBy = usePageSettings().tableStore?.getSortingBy();
	const groupByColumns = Object.keys(columnVisibility || {})
		.map((col: string) => {
			if (typeof col === 'string' && columnVisibility && columnVisibility[col]) {
				return groupByMapper[col];
			}
		})
		.filter(val => !!val) as PerformancesColumnsSnakeCase[];

	const { fetchReports } = useReportsList(reportList, {
		groupBys: [...groupByColumns],
		reportName: 'adxCampaignGroup',
		sortBy: defaultSortBy?.[0]?.id || IPerformanceOptionsSortBy.Wins,
		sortOrder: defaultSortBy?.[0]?.desc ? IPerformanceOptionsSortOrder.Desc : IPerformanceOptionsSortOrder.Asc,
		dateSegment: groupByColumns.includes(PerformancesColumnsSnakeCase.Dt)
			? IPerformanceOptionsDateSegments.Daily
			: IPerformanceOptionsDateSegments.None,
		submit: true,
	});

	useReportsList(metricsStore, {
		groupBys: [PerformancesColumnsSnakeCase.Dt],
		reportName: 'adxCampaignGroup',
		dateSegment: IPerformanceOptionsDateSegments.Daily,
	});

	const { SelectedActions, EntityActions, IndexActions, FiltersActions } = useCampaignGroupActions({
		debugProps: actionsDebugProps,
	});
	const { columns } = useCampaignGroupHeaders({ EntityActions, summary: reportList.getData()?.footer });

	const { metrics, xLabels } = useMetrics(metricsStore.getData());

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Campaign Groups</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
				</PagebarActions>
			</Pagebar>
			<Card full debugProps={{ dataAttrs: [new DataAttribute('id', 'chart')] }}>
				<Chart
					isLoading={metricsStore.getIsLoading()}
					metrics={metrics}
					labels={xLabels}
					legendOptions={Object.keys(availablePerformanceToMetrics)}
				/>
			</Card>
			<SpacerY y={2} />
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				debugProps={tableDebugProps}
				data={reportList.getData()?.data || []}
				isLoading={campaignGroupsStore.getListStore().getIsLoading()}
				fetchReports={fetchReports}
				isGlobalFilter={false}
				isSummary={true}>
				{() => {
					return {
						IndexActions,
						FiltersActions,
						SelectedActions,
					};
				}}
			</ControlledTable>
			<Outlet />
		</Page>
	);
});
